import { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { ShellSubrail, type ShellSubrailItem } from "../components/shell/ShellSubrail";
import { WorkbenchLayout } from "../components/shell/WorkbenchLayout";
import {
  actionableQueueCount,
  fetchPlatformGuardAppAccessRequests,
  type PlatformGuardAppAccessRow,
} from "../data/guard-app-access";
import { useReadModel } from "../data/useReadModel";
import type { ShellOutletContext } from "../nav/AppShell";
import { PlatformAccessQueue } from "../surfaces/PlatformAccessQueue";
import { PlatformAccountControls } from "../surfaces/PlatformAccountControls";
import { PlatformSessions } from "../surfaces/PlatformSessions";
import { PlatformFeedback } from "../surfaces/PlatformFeedback";

// Platform - the founder desk (design lock 2026-08-09, platform-founder-desk mock), now the
// shell v3 workbench (Wave 1). The old pfd-tabs strip becomes the ShellSubrail, which also
// retires the page's dead ".platform" width class: the stage chrome owns the width now, so
// the desk finally uses the whole stage instead of the legacy padded column.
//
// The access queue stays first because it is the only surface here with a person waiting at
// the end of it. Nothing here reaches App Store Connect or Play: the founder adds testers by
// hand and records it, and the copy never implies an API.
//
// The queue subrail item carries a count only when it is actionable (requested + part
// added) and disappears at zero rather than showing a 0. Never red: nobody is in danger,
// someone is waiting for an app. The Play opt-in URL lives in the founder Help article and
// nowhere else in the product - this page links the runbook, it does not repeat the URL.

type PlatformSectionKey = "queue" | "feedback" | "accounts" | "devices" | "help";

const SECTION_LABELS: Record<PlatformSectionKey, string> = {
  queue: "Access queue",
  feedback: "Feedback",
  accounts: "Accounts",
  devices: "Devices and sessions",
  help: "Founder help",
};

const SECTION_ORDER: readonly PlatformSectionKey[] = [
  "queue",
  "feedback",
  "accounts",
  "devices",
  "help",
];

export function PlatformPage() {
  const { client, config, can, isSuperadmin } = useOutletContext<ShellOutletContext>();
  const { user } = useAuth();
  const navigate = useNavigate();
  // The queue read lives at page level: it drives the subrail count, the head metrics, and
  // the default section. The RPC is platform-superadmin-gated server-side; non-platform
  // users get the honest denied state.
  const queue = useReadModel<PlatformGuardAppAccessRow[]>(
    () => fetchPlatformGuardAppAccessRequests(client),
    [client],
    config.refreshMs,
  );
  const [chosenSection, setChosenSection] = useState<PlatformSectionKey | null>(null);

  const rows = queue.data ?? [];
  const actionable = actionableQueueCount(rows);
  // Default section: the queue when something is waiting on the founder, Accounts otherwise.
  // An explicit click always wins.
  const active: PlatformSectionKey =
    chosenSection ?? (queue.status === "ready" && actionable === 0 ? "accounts" : "queue");

  const subrailItems: ShellSubrailItem[] = SECTION_ORDER.map((key) => ({
    key,
    label: SECTION_LABELS[key],
    ...(key === "queue" && actionable > 0
      ? { count: actionable, countLabel: `${actionable} waiting on you` }
      : {}),
  }));

  return (
    <WorkbenchLayout
      className="wb--panes platform-page"
      subrail={
        <ShellSubrail
          eyebrow="Platform"
          items={subrailItems}
          activeKey={active}
          onSelect={(key) => setChosenSection(key as PlatformSectionKey)}
        />
      }
      head={
        <div className="wb-head">
          <div className="wb-head__row">
            <h1 className="wb-head__title">Platform</h1>
            <p className="wb-head__note">
              Superuser desk. Company admins never see this page, and nothing on it is exposed to
              an account. Every platform read and write is server-gated; session revocations queue
              for the security worker.
            </p>
          </div>
          {queue.status === "ready" ? (
            <dl className="wb-metrics" aria-label="Access queue metrics">
              <div className="wb-metric">
                <dt className="wb-metric__label">Access requests</dt>
                <dd className="wb-metric__value">{rows.length}</dd>
              </div>
              <div className="wb-metric">
                <dt className="wb-metric__label">Waiting on you</dt>
                <dd className="wb-metric__value">{actionable}</dd>
                <dd className="wb-metric__sub">
                  {actionable > 0 ? "add the tester by hand, then mark it here" : "queue is clear"}
                </dd>
              </div>
            </dl>
          ) : null}
        </div>
      }
    >
      {active === "queue" ? (
      <section className="wb-section pfd-section" aria-label="Access queue">
        <div className="wb-section__intro">
          <div className="pfd-panel__head">
            <div>
              <h2 className="wb-section__title">Mobile app access</h2>
              <p className="page__note">
                Requests from company admins. You add the store account by hand in App Store
                Connect or Play Console, then mark it here. SecOps does not talk to the stores.
              </p>
            </div>
            {queue.status === "ready" ? (
              <span className="pfd-panel__count">
                {rows.length} {rows.length === 1 ? "request" : "requests"} · {actionable} waiting on
                you
              </span>
            ) : null}
          </div>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <PlatformAccessQueue
              client={client}
              rows={rows}
              loading={queue.status === "loading"}
              loadError={queue.status === "error" ? (queue.error ?? "Could not load the access queue.") : null}
              selfUserId={user?.id ?? ""}
              timeZone={config.displayTimeZone}
              onChanged={queue.reload}
            />
          </div>
        </div>
      </section>
      ) : null}

      {active === "feedback" ? (
      <section className="wb-section pfd-section" aria-label="Product feedback">
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Product feedback</h2>
          <p className="page__note">
            Platform staff only. Read problems and ideas with the context reporters chose to
            send. This view is read-only and does not expose a status workflow to reporters.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <PlatformFeedback
              client={client}
              timeZone={config.displayTimeZone}
              pollMs={config.refreshMs}
            />
          </div>
        </div>
      </section>
      ) : null}

      {active === "accounts" ? (
      <section className="wb-section pfd-section" aria-label="Account controls">
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Accounts</h2>
          <p className="page__note">
            Pick any tenant and manage its members, Console modules, mobile flags, duty policy,
            consent, and contract freeze without switching workspaces. Every write is an
            audit-logged hosted RPC.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <PlatformAccountControls
              client={client}
              timeZone={config.displayTimeZone}
              pollMs={config.refreshMs}
              canManage={can("policy.manage")}
              canProvision={isSuperadmin}
              canRename={isSuperadmin}
              canManageMembers={isSuperadmin && can("member.manage")}
              canManageModules={isSuperadmin}
              canSupportAs={isSuperadmin}
              onSupportStarted={() => navigate("/admin", { replace: true })}
            />
          </div>
        </div>
      </section>
      ) : null}

      {active === "devices" ? (
      <section className="wb-section pfd-section" aria-label="Devices and sessions">
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Devices and sessions</h2>
          <p className="page__note">
            Active sessions across every tenant, with redacted labels - the founder
            breach-response surface. Every view of this list is audit-logged; revocations
            queue for the security worker.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <PlatformSessions
              client={client}
              selfUserId={user?.id ?? ""}
              timeZone={config.displayTimeZone}
            />
          </div>
        </div>
      </section>
      ) : null}

      {active === "help" ? (
      <section className="wb-section pfd-section" aria-label="Founder help">
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Founder help</h2>
          <p className="page__note">
            The runbooks for the manual half of this job. Superuser only, and not linked from
            any company's Help.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <div className="pfd-help-card">
              <h3>Adding a tester by hand</h3>
              <ul className="pfd-help-list">
                <li>
                  <Link to="/help?article=phone-staging-beta">
                    Phone: staging and beta vs production - the Android opt-in link and who may hold
                    it
                  </Link>
                </li>
              </ul>
              <p className="pfd-help-note">
                The Play opt-in URL lives in that article and nowhere else in the product. If it ever
                appears in a company's console, the app has quietly become self-serve and invite only
                is over.
              </p>
            </div>
          </div>
        </div>
      </section>
      ) : null}
    </WorkbenchLayout>
  );
}
